import React, { useState } from 'react';
import { Shield, Lock, User } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:5000/api' : 'https://e2b-backend.onrender.com/api');

interface AdminLoginProps {
  onLoginSuccess: (user: any, accessToken: string, refreshToken: string) => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [adminId, setAdminId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔐 관리자 로그인 시도:', adminId);
      
      const response = await fetch(`${API_URL}/auth/admin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminId: adminId.trim(), password: password.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '로그인에 실패했습니다.');
      }

      const user = data.user;
      const accessToken = data.accessToken;
      const refreshToken = data.refreshToken;

      console.log('✅ 관리자 로그인 성공');
      
      // 반드시 스토어에 먼저 저장 (이후 /admin 접근 시 user가 있어야 함)
      setAuth(
        {
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.role || 'ADMIN',
          avatar: user.avatar,
          provider: (user.provider as 'email' | 'kakao') || 'email',
          mustChangePassword: Boolean(user.mustChangePassword),
        },
        accessToken,
        refreshToken
      );
      
      // 네비게이션은 다음 틱으로 미뤄 스토어 반영이 완료된 뒤 이동
      setTimeout(() => {
        onLoginSuccess(user, accessToken, refreshToken);
      }, 0);
      
    } catch (err: any) {
      console.error('❌ 관리자 로그인 실패:', err);
      setError(err.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-500 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">e2b 관리자</h1>
          <p className="text-slate-400">관리자 전용 로그인</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Admin ID */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                관리자 ID
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={adminId}
                  onChange={(e) => setAdminId(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="admin (이메일 아님)"
                  required
                  autoComplete="username"
                />
                <p className="mt-1 text-xs text-slate-500">이메일이 아니라 관리자 ID만 입력하세요.</p>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                비밀번호
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-semibold rounded-lg hover:from-rose-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </form>

          {/* Info */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              관리자 계정은 시스템 관리자에게 문의하세요.
            </p>
          </div>
        </div>

        {/* Back to Main */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            ← 메인 페이지로 돌아가기
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

